import { PostMedia } from "../types/postType";

export interface MediaUploadConfig {
  MAX_IMAGE_SIZE: number;
  MAX_VIDEO_SIZE: number;
  MAX_MEDIA_COUNT: number;
}

const DEFAULT_CONFIG: MediaUploadConfig = {
  MAX_IMAGE_SIZE: 10 * 1024 * 1024, // 10MB
  MAX_VIDEO_SIZE: 50 * 1024 * 1024, // 50MB
  MAX_MEDIA_COUNT: 10
};

type AspectRatio = 'vertical' | 'horizontal' | 'square';

export const getAspectRatio = (width: number, height: number): AspectRatio => {
  const ratio = width / height;
  if (ratio > 1.1) return 'horizontal';
  if (ratio < 0.9) return 'vertical';
  return 'square';
};

export const getImageStyles = (
  media: PostMedia, 
  mediaCount: number = 1,
  postType?: string
): React.CSSProperties => {
  const ratio = media.width / media.height;
  const aspectRatio = getAspectRatio(media.width, media.height);

  // Single media styles
  if (mediaCount === 1) {
    if (aspectRatio === 'vertical') {
      const width = Math.round((media.width / media.height) * 430);
      return { height: '430px', width: `${width}px` };
    } else if (aspectRatio === 'horizontal') {
      const height = Math.round((media.height / media.width) * 358);
      return { width: '358px', height: `${height}px` };
    } else {
      return { width: '100%', maxWidth: '250px', aspectRatio: '1/1' };
    }
  }

  // Multiple media styles
  const FIXED_HEIGHT = 178; // Fixed height for all media in a multi-media post
  
  if (aspectRatio === 'vertical') {
    const width = Math.round((media.width / media.height) * FIXED_HEIGHT);
    return {
      height: `${FIXED_HEIGHT}px`,
      width: `${width}px`,
      maxWidth: '250px',
      minWidth:'120px',
      objectFit: 'cover' as 'cover'
    };
  } else if (aspectRatio === 'horizontal') {
    // For horizontal images, constrain by max width
    const calculatedHeight = Math.min(FIXED_HEIGHT, Math.round((media.height / media.width) * 250));
    return {
      maxWidth: '250px',
      height: `${FIXED_HEIGHT}px`,
      width: 'auto',
      objectFit: 'cover' as 'cover'
    };
  } else {
    // Square images
    return {
      height: `${FIXED_HEIGHT}px`,
      width: `${FIXED_HEIGHT}px`,
      maxWidth: '250px',
      objectFit: 'cover' as 'cover'
    };
  }
};

/**
 * Process file uploads using FileReader for better iOS compatibility
 */
export const processMediaFiles = async (
  files: FileList,
  config: MediaUploadConfig = DEFAULT_CONFIG
): Promise<{ success: PostMedia[]; errors: string[] }> => {
  console.log("=== PROCESS MEDIA FILES STARTED ===");
  console.log("Files received:", files.length);
  
  const results: {
    success: PostMedia[];
    errors: string[];
  } = {
    success: [],
    errors: []
  };

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    console.log(`Processing file ${i + 1}/${files.length}:`, {
      name: file.name,
      type: file.type,
      size: file.size
    });
    
    try {
      // Check file size
      const maxSize = file.type.startsWith('video/') ? config.MAX_VIDEO_SIZE : config.MAX_IMAGE_SIZE;
      if (file.size > maxSize) {
        const errorMsg = `File ${file.name} is too large. Maximum size is ${file.type.startsWith('video/') ? '50MB' : '10MB'}`;
        console.log(errorMsg);
        results.errors.push(errorMsg);
        continue;
      }
      
      // Determine media type
      let mediaType: 'image' | 'video' | 'gif';
      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      
      if (file.type.startsWith('image/')) {
        mediaType = file.type.includes('gif') ? 'gif' : 'image';
      } else if (file.type.startsWith('video/')) {
        mediaType = 'video';
      } else if (file.type.includes('heic') || file.type.includes('heif')) {
        mediaType = 'image'; // Handle iOS HEIC
      } else if (['jpg', 'jpeg', 'png', 'heic', 'heif'].includes(extension)) {
        mediaType = extension === 'gif' ? 'gif' : 'image';
      } else if (['mp4', 'mov', 'avi', 'quicktime'].includes(extension)) {
        mediaType = 'video';
      } else {
        const errorMsg = `File ${file.name} has unsupported type`;
        console.log(errorMsg);
        results.errors.push(errorMsg);
        continue;
      }
      
      // Use FileReader for better iOS compatibility
      const media = await new Promise<PostMedia>((resolve, reject) => {
        const fileReader = new FileReader();
        
        fileReader.onload = (event) => {
          try {
            const dataUrl = event.target?.result as string;
            console.log(`File ${i + 1} read successfully`);
            
            const media: PostMedia = {
              url: dataUrl,
              file,
              media_type: mediaType,
              width: 300, // Default width
              height: 300  // Default height
            };
            
            resolve(media);
          } catch (error) {
            console.error(`Error processing file ${i + 1} data:`, error);
            reject(error);
          }
        };
        
        fileReader.onerror = (error) => {
          console.error(`FileReader error for file ${i + 1}:`, error);
          reject(error);
        };
        
        // Read as data URL for better cross-platform compatibility
        fileReader.readAsDataURL(file);
      });
      
      console.log(`Successfully processed file ${i + 1}:`, {
        mediaType: media.media_type,
        url: media.url.substring(0, 30) + '...',
        hasFile: !!media.file
      });

      results.success.push(media);
    } catch (error) {
      console.error(`Error processing file ${i + 1}:`, file.name, error);
      results.errors.push(`Failed to process ${file.name}. Please try again.`);
    }
  }

  console.log("Media processing complete:", {
    successCount: results.success.length,
    errorsCount: results.errors.length
  });
  console.log("=== PROCESS MEDIA FILES COMPLETED ===");
  
  return results;
};

/**
 * Handle camera capture and convert to PostMedia
 */
export const processCameraCapture = (imageSrc: string): PostMedia => {
  // Convert base64 to file
  const byteString = atob(imageSrc.split(',')[1]);
  const mimeString = imageSrc.split(',')[0].split(':')[1].split(';')[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  
  const file = new File([ab], 'capture.jpg', { type: mimeString });
  
  return {
    url: imageSrc,
    file,
    media_type: 'image',
    width: 300,
    height: 300
  };
}; 